import { get } from 'lodash'
import { createSlice } from '@reduxjs/toolkit'
import {
  getShippingInfo,
  updateSession,
  deleteSession,
  setDeliveryPreferences,
  getPhotoPODImages,
  getOptedInStatus,
} from 'api/shipping'
import {
  getSessionValidity,
  removeCookieFromLocalStorage,
  setCookieToLocalStorage,
  getCookieIDFromLocalStorage,
} from 'utils/helperFunctions'
import { selectTrackingDetails } from './shippingSelectors'

const shipmentPinPathParam = window.location.pathname.split('/')[4]
const initialState = {
  info: null,
  loading_info: false,
  error_info: null,
  selectedPackagePinInfo: null,
  isVerifiedUser: getSessionValidity(shipmentPinPathParam),
  allowRenewSession: false,
  isAuthenticated: false,
  isGuest: false,
  userEmail: '',
  isOtpVerificationRequired: false,
  recipientPhoneNumber: '',
}

export const shippingSlice = createSlice({
  name: 'shipping',
  initialState,
  reducers: {
    setShippingInfo: (state, action) => {
      state.info = action.payload
    },
    setSelectedPackagePinInfo: (state, action) => {
      state.selectedPackagePinInfo = action.payload
    },
    setShippingPreferences: (state, action) => {
      state.info.buzzer_code = action.payload.buzzer_code
      state.info.additional_delivery_instructions = action.payload.additional_delivery_instructions
      state.info.delivery_preference = action.payload.delivery_preference
      state.info.preference_id = action.payload.preference_id
      state.info.preference_modified = action.payload.preference_modified
      state.info.signature = action.payload.signature
      state.info.remote_signature_preference = action.payload.remote_signature_preference
    },
    loadingShippingInfo: (state, action) => {
      state.loading_info = action.payload
    },
    errorLoadingShippingInfo: (state, action) => {
      state.error_info = action.payload
    },
    setSelectedPin: (state, action) => {
      state.selectedPin = action.payload
    },
    setIsVerifiedUser: (state, action) => {
      state.isVerifiedUser = action.payload
    },
    setAllowRenewSession: (state, action) => {
      state.allowRenewSession = action.payload
    },
    setPhotoPOD: (state, action) => {
      state.info.photoPODImages = action.payload
    },
    setOptedInStatusDetails: (state, action) => {
      state.optedInStatusDetails = action.payload
    },
    setGuestHashPin: (state, action) => {
      state.guestHashPin = action.payload
    },
    setIsAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload
    },
    setIsGuest: (state, action) => {
      state.isGuest = action.payload
    },
    setUserEmail: (state, action) => {
      state.userEmail = action.payload
    },
    setOtpVerifyRequired: (state, action) => {
      state.isOtpVerificationRequired = action.payload
    },
    setPhoneNumForOtpVerify: (state, action) => {
      state.recipientPhoneNumber = action.payload
    },
  },
})

const {
  setShippingInfo,
  loadingShippingInfo,
  errorLoadingShippingInfo,
  setSelectedPackagePinInfo,
  setShippingPreferences,
  setSelectedPin,
  setPhotoPOD,
  setGuestHashPin,
  setIsAuthenticated,
  setIsGuest,
  setUserEmail,
  setOtpVerifyRequired,
  setPhoneNumForOtpVerify,
} = shippingSlice.actions

export const { setIsVerifiedUser, setAllowRenewSession, setOptedInStatusDetails } =
  shippingSlice.actions

export const getShippingInformation = (shipmentPin, lang, prn) => async (dispatch) => {
  dispatch(loadingShippingInfo(true))
  let shippingInfo = null
  try {
    shippingInfo = await getShippingInfo(shipmentPin, lang)
    const { data } = shippingInfo

    const selectedPackagePinInfo = data.trackingHistoryData[data.shipmentPin]
    dispatch(setShippingInfo(data))
    dispatch(setSelectedPackagePinInfo(selectedPackagePinInfo))
    dispatch(setSelectedPin(data.shipmentPin))
    const optedInStatusDetailsResp = await getOptedInStatus(
      data.id,
      data.shipmentPin,
      data.hashedPin,
      data.customerPhoneNumber,
      prn
    )
    dispatch(setOptedInStatusDetails(optedInStatusDetailsResp))
  } catch (e) {
    if (get(e, 'response.data', null)) {
      dispatch(errorLoadingShippingInfo(e.response.data))
    }
  } finally {
    dispatch(loadingShippingInfo(false))
  }
}

export const updateTrackingDetails = (pin) => (dispatch, state) => {
  const trackingDetails = selectTrackingDetails(state())[pin]
  dispatch(setSelectedPackagePinInfo(trackingDetails))
  dispatch(setSelectedPin(pin))
}

export const updatePhotoPODImagesByPin = (pin) => async (dispatch) => {
  const photoPODImages = await getPhotoPODImages(pin)
  dispatch(setPhotoPOD(photoPODImages.data))
}

export const endSession = () => async (dispatch, getState) => {
  const { shipmentPin, hashedPin } = getState()?.shipping?.info || {}
  removeCookieFromLocalStorage(hashedPin)
  const result = await deleteSession(shipmentPin)
  if (result.data.response === 'SESSION_DELETED') {
    dispatch(setIsVerifiedUser(false))
  }
}
export const renewSession = () => async (dispatch, getState) => {
  const { id, hashedPin } = getState()?.shipping?.info || {}
  const result = await updateSession(id, hashedPin)
  if (result.data.response === 'SESSION_UPDATED') {
    const { expiryTime, updateExpTime } = result.data
    setCookieToLocalStorage(hashedPin, getCookieIDFromLocalStorage(hashedPin), expiryTime)
    dispatch(setAllowRenewSession(updateExpTime))
  }
}

export const setShipmentPreferences =
  (
    additionalDeliveryInstructions,
    buzzerCode,
    dropOffLocation,
    signaturePreference,
    previousSignaturePref
  ) =>
  async (dispatch, getState) => {
    const { id, hashedPin } = getState().shipping.info
    const result = await setDeliveryPreferences(
      'SHIPMENT',
      'en',
      id,
      additionalDeliveryInstructions,
      buzzerCode,
      dropOffLocation,
      signaturePreference,
      previousSignaturePref,
      hashedPin
    )
    dispatch(setShippingPreferences(result.data))
  }

export const setHashPin = (hashPin) => (dispatch) => {
  dispatch(setGuestHashPin(hashPin))
}

export const setHasSession = (isAuthenticated) => (dispatch) => {
  dispatch(setIsAuthenticated(isAuthenticated))
}

export const setOtpVerificationRequired = (isOtpVerificationRequired) => (dispatch) => {
  dispatch(setOtpVerifyRequired(isOtpVerificationRequired))
}

export const setPhoneNumForOtpVerification = (recipientPhoneNumber) => (dispatch) => {
  dispatch(setPhoneNumForOtpVerify({ recipientPhoneNumber }))
}

export const setIsGuestMode = (isGuest) => (dispatch) => {
  dispatch(setIsGuest(isGuest))
}

export const setEmailAddress = (email) => (dispatch) => {
  dispatch(setUserEmail(email))
}

export default shippingSlice.reducer
